import {
  Box,
  HStack,
  IconButton,
  Select,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { FaChevronCircleDown } from "react-icons/fa";
import { useState, useEffect } from "react";
import { errorFromResponse } from "./utils/apiErrors";

const RegisterEntrySelect = ({
  registers,
  registerLoading,
  setSignalDataArray,
}) => {
  const toast = useToast();
  const [selectedRegisterId, setSelectedRegisterId] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Fetch entries when a register is selected
  useEffect(() => {
    const fetchEntries = async () => {
      if (!selectedRegisterId) {
        setEntries([]);
        setSelectedEntryId(""); // Reset entry selection when register changes
        return;
      }

      setEntriesLoading(true);
      setSelectedEntryId(""); // Reset entry selection when register changes
      try {
        const response = await fetch(
          `https://registers.poc.mrc.ai/api/registerentries/headers?registerId=${selectedRegisterId}`
        );
        if (!response.ok) {
          throw await errorFromResponse(response, "Failed to fetch register entries");
        }
        const data = await response.json();
        setEntries(data);
      } catch (error) {
        console.error("Error fetching entries:", error);
        toast({
          title: "Failed to load entries",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setEntries([]);
      } finally {
        setEntriesLoading(false);
      }
    };

    fetchEntries();
  }, [selectedRegisterId, toast]);

  const handleSelectTemplate = async () => {
    console.log("🔍 Select Template clicked");

    try {
      // The list holds header objects only; fetch the full entry for its data
      if (!entries.some((entry) => entry._id === selectedEntryId)) {
        throw new Error("Register entry not found");
      }
      const response = await fetch(
        `https://registers.poc.mrc.ai/api/registerentries/${selectedEntryId}`
      );
      if (!response.ok) {
        throw await errorFromResponse(response, "Failed to load register entry");
      }
      const fullEntry = await response.json();
      console.log("📗 Found Register Entry:", fullEntry);

      const newSignalData = {
        id: crypto.randomUUID(),
        created: new Date().toISOString(),
        name: fullEntry.name || "",
        description: fullEntry.description || "",
        type: fullEntry.type || "",
        classCode: fullEntry.classCode || "",
        objectType: fullEntry.objectType || "",
        references: fullEntry.references || [],
        classifications: fullEntry.classifications || [],
        amounts: fullEntry.amounts || [],
        dateTimes: fullEntry.dateTimes || [],
        parties: fullEntry.parties || [],
        addresses: fullEntry.addresses || [],
        text: fullEntry.text || [],
      };

      console.log("✅ Adding signalData:", newSignalData);
      setSignalDataArray((prev) => [...prev, newSignalData]);

      toast({
        title: "Template loaded",
        description: `Added template: ${fullEntry.name}`,
        status: "success",
        duration: 2000,
      });
    } catch (err) {
      console.error("❌ Error during template load:", err);
      toast({
        title: "Failed to load template",
        description: err.message,
        status: "error",
      });
    }
  };

  return (
    <Box
      p="2"
      mb="4"
      borderWidth="1px"
      borderRadius="md"
      borderColor="gray.200"
      backgroundColor="whiteAlpha.800">
      <Text fontWeight="bold" fontStyle="italic" fontSize="lg">
        Signal Data Templates
      </Text>

      <HStack>
        <Select
          width="300px"
          placeholder="Select Register"
          isDisabled={registerLoading}
          value={selectedRegisterId}
          onChange={(e) => setSelectedRegisterId(e.target.value)}>
          {registers.map((reg) => (
            <option key={reg._id} value={reg._id}>
              {reg.name}
            </option>
          ))}
        </Select>

        <Select
          width="300px"
          placeholder="Select Register Entry"
          isDisabled={registerLoading || !selectedRegisterId || entriesLoading}
          value={selectedEntryId}
          onChange={(e) => setSelectedEntryId(e.target.value)}>
          {entries.map((entry) => (
            <option key={entry._id} value={entry._id}>
              {entry.name}
            </option>
          ))}
        </Select>

        <Tooltip label="Select Template">
          <IconButton
            icon={<FaChevronCircleDown />}
            onClick={handleSelectTemplate}
          />
        </Tooltip>
      </HStack>
    </Box>
  );
};

export default RegisterEntrySelect;
